const pick = require('lodash/pick')
const { v4 } = require('uuid')
const get = require('lodash/get')
const capitalize = require('lodash/capitalize')
const { QuickbooksInterface } = require('./quickbooks-interface')
const QuickbooksLocationAdapter = require('./quickbooks-location-adapter')
const QuickbooksTransactionsAdapter = require('./quickbooks-transactions-adapter')
const QuickbooksRawTransactionsAdapter = require('./quickbooks-raw-transactions-adapter')
const QuickbooksTransformAdapter = require('./quickbooks-transform-adapter')
const { transformInvoiceToDeposit, transformPaymentPlanToDeposit } = require('./tools/transform-to-deposit')
const transformToPayment = require('./tools/transform-to-payment')
const { constructLineItem } = require('./tools/journal-entry-utils')
const utils = require('./tools/utils')
const { EntitiesApi } = require('./entities/api.js')
const { syncQboObjs, syncPaymentPlanBalances } = require('../finances/sync')
const { getCustomVatLineCountries } = require('../invoices/tools')

const companyCodes = [{
  companyCode: 'KE',
  geoId: 'country:ke',
  currency: 'KES',
  currencyName: 'Kenyan Shilling'
}, {
  companyCode: 'NG',
  geoId: 'country:ng',
  currency: 'NGN',
  currencyName: 'Nigerian Naira'
}]

const getCompany = (locationId) => {
  const company = companyCodes.find(company => locationId.startsWith(company.geoId))
  if (!company) {
    const error = new Error(`Cant find company code for location ${locationId}`)
    error.status = 400
    throw error
  }
  return company
}

class QuickbooksAdapter {
  constructor (
    state,
    pgConnection,
    quickbooksConfigs,
    isomorphicFetch,
    quickbooksOAuthClient,
    notificationsApi,
    logger,
    paymentPlanApi
  ) {
    const { user = {} } = state
    const username = user.name
    this.tools = {
      ...utils
    }

    this.CURRENCY_TO_COMPANY_CODE = {
      NGN: 'ng',
      KES: 'ke'
    }

    // Contains map of possible qbo error codes and response to send back to our clients
    // Quicbooks error codes are documented in below url
    // https://developer.intuit.com/app/developer/qbo/docs/develop/troubleshooting/error-codes
    this.ERROR_MAP = {
      '610': (entity) => {
        return {
          message: `${capitalize(entity)} not found, please contact support`,
          status: 404
        }
      }
    }
    this.MAX_QBO_DOC_NUMBER_LENGTH = 21

    this.paymentPlan = paymentPlanApi
    this.logger = logger
    if (quickbooksConfigs && quickbooksConfigs.quickbooksEnabled) {
      this.quickbooksInterface = new QuickbooksInterface(
        pgConnection,
        quickbooksConfigs,
        isomorphicFetch,
        quickbooksOAuthClient,
        logger
      )

      this.entities = new EntitiesApi(this)
    }

    if (pgConnection) {
      this.transactions = new QuickbooksTransactionsAdapter(
        pgConnection,
        username
      )

      this.rawTransactions = new QuickbooksRawTransactionsAdapter(
        pgConnection,
        username
      )

      this.transform = new QuickbooksTransformAdapter(
        pgConnection,
        username,
        logger,
        this.rawTransactions,
        this.quickbooksInterface,
        notificationsApi,
        this.tools
      )

      this.location = new QuickbooksLocationAdapter(
        pgConnection,
        username
      )
      this.pgConnection = pgConnection
    }
    // For our quickbooks sandbox, the default currency setting is set to USD
    // we do this so that staging and dev can work when testing
    if (quickbooksConfigs && quickbooksConfigs.environment === 'sandbox') {
      companyCodes.forEach(company => {
        company.currency = 'USD'
        company.currencyName = 'United States Dollar'
      })
    }
  }

  async getExistingDeposit ({ startDate, reference, companyCode, customerId }) {
    // Why we offset the payments original start date? => https://github.com/fielded/field-supply/issues/17999#issuecomment-2862231341
    const dateFormat = new Date(startDate)
    const timeZoneDifferenceMs = 24 * 3600 * 1000
    const paymentCreatedAt = new Date(dateFormat.getTime() - timeZoneDifferenceMs)
    const deposits = await this.entities.report.getCustomerTransactionList({
      companyCode,
      customerId,
      txnTypes: ['Deposit'],
      startDate: paymentCreatedAt.toJSON(),
      endDate: new Date().toJSON()
    })

    return deposits.find(d => d.PrivateNote === reference)
  }

  async createPaymentPlanDeposit ({
    transaction,
    paymentPlan,
    paymentInstallment,
    isPaystackDeposit,
    instalments = [],
    qboLocation
  }) {
    await this.quickbooksInterface.init()

    const {
      paymentPlanFeesAccountId,
      remitaFeesAccountId,
      classRef
    } = this.quickbooksInterface.accountIds[paymentPlan.company_code]

    const depositAccountId = this.quickbooksInterface.accountIds[paymentPlan.company_code][isPaystackDeposit ? 'paystackDepositAccountId' : 'depositAccountId']

    let quickBooksLocation = qboLocation
    if (!quickBooksLocation) {
      const { results } = await this.location.getList({
        ordering: '-created_at',
        filter: {
          location_id: paymentPlan.location_id,
          company_code: paymentPlan.company_code
        },
        limit: 1
      })
      quickBooksLocation = results[0]
    }

    const {Customer: qboCustomer} = await this.quickbooksInterface.get(
      quickBooksLocation.company_code,
      `/customer/${quickBooksLocation.quickbooks_customer_id}`
    )

    const { Account: depositAccount } = await this.quickbooksInterface.get(
      paymentPlan.company_code,
      `/account/${depositAccountId}`
    )

    const {Account: loanReceivableAccount} = await this.quickbooksInterface.get(
      paymentPlan.company_code,
      `/account/${paymentPlan.quickbooks_account_id}`
    )

    const lrAccountFeeDeposits = await this.rawTransactions.getLoanAccountFeeDeposits(
      [paymentPlan.quickbooks_account_id],
      paymentPlan.company_code
    )

    let remitaFeesAccount = null
    if (remitaFeesAccountId && transaction) {
      const { Account } = await this.quickbooksInterface.get(
        paymentPlan.company_code,
        `/account/${remitaFeesAccountId}`
      )
      remitaFeesAccount = Account
    }

    let paymentPlanFeesAccount = null
    if (paymentPlan.service_fee && paymentPlanFeesAccountId) {
      const { Account } = await this.quickbooksInterface.get(
        paymentPlan.company_code,
        `/account/${paymentPlanFeesAccountId}`
      )
      paymentPlanFeesAccount = Account
    }

    const depositData = transformPaymentPlanToDeposit({
      transaction,
      qboCustomer,
      paymentPlan,
      paymentInstallment,
      depositAccount,
      loanReceivableAccount,
      remitaFeesAccount,
      paymentPlanFeesAccount,
      classRef,
      lrAccountFeeDeposits,
      instalmentsFullyPaid: instalments.filter(i => i.is_paid).length
    })
    const deposit = await this.quickbooksInterface.post(paymentPlan.company_code, 'deposit', depositData)
    await Promise.all([
      this.transform.transformAndSave(paymentPlan.company_code, { Deposit: [deposit.Deposit] }),
      syncQboObjs(this.pgConnection, paymentPlan.company_code, { deposit: [deposit.Deposit] }, this.entities)
    ])
    return {
      deposit
    }
  }

  async createPaymentInvoiceDeposit ({
    transaction,
    quickbooksInvoice,
    qboCustomer,
    quickbooksLocation,
    depositAccount,
    payment,
    remitaFee
  }) {
    const {
      accountReceivableIds,
      remitaFeesAccountId,
      classRef
    } = this.quickbooksInterface.accountIds[quickbooksLocation.company_code]
    const [accountReceivableId] = accountReceivableIds
    const { Account: accountReceivable } = await this.quickbooksInterface.get(
      quickbooksLocation.company_code,
      `/account/${accountReceivableId}`
    )

    const { Account: remitaFeesAccount } = await this.quickbooksInterface.get(
      quickbooksLocation.company_code,
      `/account/${remitaFeesAccountId}`
    )

    const depositData = transformInvoiceToDeposit({
      transaction,
      quickbooksInvoice,
      qboCustomer,
      depositAccount,
      accountReceivable,
      remitaFeesAccount,
      classRef,
      payment,
      remitaFee
    })

    const { Deposit } = await this.quickbooksInterface.post(quickbooksLocation.company_code, 'deposit', depositData)

    return {
      deposit: Deposit,
      qboCustomer,
      depositAccount
    }
  }

  async createPayment ({
    quickbooksInvoice,
    totalAmount
  }) {
    const {results} = await this.location.getList({
      ordering: '-created_at',
      filter: {
        location_id: quickbooksInvoice.location_id,
        company_code: quickbooksInvoice.company_code
      }
    })
    if (!results.length) {
      const error = new Error(`Failed booking: quickbooks_location ${quickbooksInvoice.location_id} not found`)
      error.status = 400
      throw error
    }

    const quickbooksLocation = results[0]
    await this.quickbooksInterface.init()

    const {
      depositAccountId
    } = this.quickbooksInterface.accountIds[quickbooksLocation.company_code]

    const {Customer: qboCustomer} = await this.quickbooksInterface.get(
      quickbooksLocation.company_code,
      `/customer/${quickbooksLocation.quickbooks_customer_id}`
    )

    const { Account: depositAccount } = await this.quickbooksInterface.get(
      quickbooksLocation.company_code,
      `/account/${depositAccountId}`
    )

    const paymentData = {
      TotalAmt: totalAmount,
      CustomerRef: {
        value: qboCustomer.Id,
        name: qboCustomer.DisplayName
      }
    }
    const { Payment } = await this.quickbooksInterface.post(
      quickbooksInvoice.company_code,
      `payment`,
      paymentData
    )

    return {
      payment: Payment,
      quickbooksLocation,
      qboCustomer,
      depositAccount
    }
  }

  async updatePayment ({
    quickbooksInvoice,
    payment,
    totalAmount
  }) {
    const transformedData = transformToPayment({
      payment: pick(payment, ['TotalAmt', 'CurrencyRef', 'Id', 'SyncToken', 'CustomerRef', 'MetaData', 'LinkedTxn']),
      quickbooksInvoice,
      totalAmount
    })

    const {Payment: finalPayment} = await this.quickbooksInterface.post(
      quickbooksInvoice.company_code,
      `payment`,
      transformedData
    )
    return {
      payment: finalPayment
    }
  }

  async getCustomerByLocation (location) {
    const company = getCompany(location._id)
    const { QueryResponse } = await this.quickbooksInterface.get(
      company.companyCode,
      `query?query=${encodeURIComponent(`select * from Customer Where DisplayName = '${location.name}'`)}`
    )
    return get(QueryResponse, 'Customer.0')
  }

  async getAccountByName ({companyCode, accountName}) {
    const { QueryResponse } = await this.quickbooksInterface.get(
      companyCode,
      `query?query=${encodeURIComponent(`select * from Account Where Name = '${accountName}'`)}`
    )
    return get(QueryResponse, 'Account.0')
  }

  async createCustomer (location) {
    const company = getCompany(location._id)
    const qboData = this.quickbooksInterface.transformLocationToQboCustomer(location)
    const date = new Date().toJSON()
    let customer = await this.getCustomerByLocation(location)
    if (!customer) {
      const { Customer } = await this.quickbooksInterface.post(company.companyCode, 'customer', qboData)
      customer = Customer
    }
    return this.location.create({
      id: v4(),
      created_at: date,
      updated_at: date,
      company_code: company.companyCode,
      quickbooks_customer_id: customer.Id,
      quickbooks_sync_token: customer.SyncToken, // SyncToken is required for doing update
      location_id: location.additionalData.uuid
    })
  }

  async createAccount ({
    locationFsid,
    accountName,
    accountType,
    accounSubType,
    description,
    classification,
    parentAccountId,
    isSubAccount
  }) {
    const company = getCompany(locationFsid)
    const newAccount = {
      Name: accountName, // is unique
      AccountType: accountType,
      AccountSubType: accounSubType,
      Description: description,
      CurrentBalance: 0,
      Classification: classification,
      SubAccount: false,
      CurrencyRef: {
        value: company.currency,
        name: company.currencyName
      }
    }
    if (isSubAccount) {
      newAccount.SubAccount = true
      newAccount.ParentRef = {
        value: parentAccountId
      }
    }

    const { Account } = await this.quickbooksInterface.post(
      company.companyCode,
      `account`,
      newAccount
    )
    return Account
  }

  async createJournalEntry ({location, description, loanReceivable, accountReceivable, amount, docNumber}) {
    const company = getCompany(location._id)
    const uuid = location.uuid || location.additionalData.uuid

    const { results } = await this.location.getList({
      filter: {
        location_id: uuid,
        company_code: company.companyCode
      }
    })
    if (!results || !results.length) {
      const error = new Error(`Cant find quickbooks customer for location ${uuid}`)
      error.status = 400
      throw error
    }

    const entity = {
      Type: 'Customer',
      EntityRef: {
        value: results[0].quickbooks_customer_id,
        name: location.name
      }
    }
    const accountName = `${accountReceivable.Name} - ${company.currency}` // account to be credited
    const creditAccount = constructLineItem({
      description,
      amount,
      postingType: 'Credit',
      entity,
      accountId:
      accountReceivable.Id,
      accountName
    })
    const debitAccount = constructLineItem({
      description,
      amount,
      postingType: 'Debit',
      entity,
      accountId: loanReceivable.Id,
      accountName: loanReceivable.Name
    })

    let parsedDocNumber = docNumber || `Auto PP LrId-${loanReceivable.Id}`
    if (parsedDocNumber.length > this.MAX_QBO_DOC_NUMBER_LENGTH) {
      parsedDocNumber = `LrId-${loanReceivable.Id}`
    }

    const newJournalEntry = {
      CurrencyRef: {
        value: company.currency,
        name: company.currencyName
      },
      Line: [creditAccount, debitAccount],
      DocNumber: parsedDocNumber
    }

    const { JournalEntry } = await this.quickbooksInterface.post(
      company.companyCode,
      `journalentry`,
      newJournalEntry
    )
    return JournalEntry
  }

  // via a payment
  async linkInvoiceToJournalEntry ({location, amount, txnId, journalId}) {
    const company = getCompany(location._id)
    const uuid = location.uuid || location.additionalData.uuid

    const { results } = await this.location.getList({
      filter: {
        location_id: uuid,
        company_code: company.companyCode
      }
    })
    if (!results) {
      const error = new Error(`Cant find quickbooks customer for location ${uuid}`)
      error.status = 400
      throw error
    }

    const newPayment = {
      CustomerRef: { value: results[0].quickbooks_customer_id, name: location.name },
      CurrencyRef: { value: company.currency, name: company.currencyName },
      Line: [
        {
          Amount: amount,
          LinkedTxn: [{TxnId: journalId, TxnType: 'JournalEntry'}]
        },
        {
          Amount: amount,
          LinkedTxn: [{TxnId: txnId, TxnType: 'Invoice'}]
        }
      ]
    }
    const { Payment } = await this.quickbooksInterface.post(
      company.companyCode,
      `payment`,
      newPayment
    )
    return Payment
  }

  async convertInvoiceToLoan ({
    locationName,
    loanQboAccountId,
    qboDocNumber,
    invoiceTxnId,
    companyCode,
    locationUuid,
    locationFsid,
    paymentPlanId,
    journalEntryId,
    paymentId,
    pastDueAmount,
    feeEvent,
    invoiceApi,
    customVatLineCountries
  }) {
    const parentAccounts = utils.getParentAccounts(this.quickbooksInterface.accountIds, companyCode)
    const parsedName = utils.removeSpecialChars(locationName)
    const loanAccountName = `L/R - ${parsedName} - ${qboDocNumber}`
    const cacheQboIds = new Map()
    const qboEntities = { JournalEntry: null, Payment: null }

    try {
      let loanAccount = await this.getAccountByName({
        companyCode: companyCode,
        accountName: loanAccountName
      })

      if (!loanQboAccountId || !loanAccount) {
        if (!loanAccount) {
          loanAccount = await this.createAccount({
            locationFsid: locationFsid,
            accountName: loanAccountName,
            accountType: 'Other Current Asset',
            accounSubType: 'LoansToOthers',
            classification: 'Asset',
            description: `Payment Plan for ${locationName.slice(0, 60)} ${qboDocNumber}`,
            isSubAccount: true,
            parentAccountId: parentAccounts.loanReceivableId
          })
        }
        await this.paymentPlan.updatePaymentPlan({id: paymentPlanId, quickbooks_account_id: loanAccount.Id})
      }
      cacheQboIds.set('accountId', loanAccount.Id)

      if (journalEntryId) {
        try {
          const journalEntry = await this.entities.journalEntry.get(companyCode, journalEntryId)
          qboEntities.JournalEntry = [journalEntry]
          cacheQboIds.set('journalId', journalEntry.Id)
        } catch (error) {
          this.logger.warn(`Get JournalEntry failed: `, error)
        }
      }

      if (!journalEntryId || !qboEntities.JournalEntry) {
        const journalEntry = await this.createJournalEntry({
          location: {
            _id: locationFsid,
            uuid: locationUuid
          },
          description: `PP - ${qboDocNumber}`,
          accountReceivable: {
            Id: parentAccounts.accountReceivableId,
            Name: 'Accounts Receivable:Accounts Receivable (A/R)'
          },
          loanReceivable: {
            Id: loanQboAccountId || cacheQboIds.get('accountId'),
            Name: loanAccountName
          },
          amount: pastDueAmount,
          docNumber: null
        })
        cacheQboIds.set('journalId', journalEntry.Id)
        qboEntities.JournalEntry = [journalEntry]
        await this.paymentPlan.updatePaymentPlan({id: paymentPlanId, quickbooks_journal_entry_id: journalEntry.Id})
      }

      if (paymentId) {
        try {
          const payment = await this.entities.payment.get(companyCode, paymentId)
          qboEntities.Payment = [payment]
        } catch (error) {
          this.logger.warn(`Get Payment failed: `, error)
        }
      }

      if (!paymentId || !qboEntities.Payment) {
        const payment = await this.linkInvoiceToJournalEntry({
          location: {
            _id: locationFsid,
            uuid: locationUuid
          },
          amount: pastDueAmount,
          txnId: invoiceTxnId,
          journalId: journalEntryId || cacheQboIds.get('journalId')
        })
        qboEntities.Payment = [payment]
        await this.paymentPlan.updatePaymentPlan({id: paymentPlanId, quickbooks_payment_id: payment.Id})
      }

      const invoice = await this.entities.invoice.get(companyCode, invoiceTxnId)
      qboEntities.Invoice = [invoice]
      if (!qboEntities.JournalEntry) {
        const journalEntry = await this.entities.journalEntry.get(companyCode, journalEntryId)
        qboEntities.JournalEntry = [journalEntry]
      }

      if (!qboEntities.Payment) {
        const payment = await this.entities.payment.get(companyCode, paymentId)
        qboEntities.Payment = [payment]
      }

      // If a service fee event rows have been written on payment plan creation
      // We create resulting quickbooks invoice for the given payment plans service fee
      if (feeEvent && invoiceApi) {
        const feeInvoiceData = await invoiceApi.pg.eventsToInvoice({ locationId: locationUuid, eventDates: [feeEvent.date] })
        const invoiceId = await invoiceApi.pg.insertInvoice(feeInvoiceData)
        const { invoice, lines } = await invoiceApi.pg.getInvoice(invoiceId)
        await invoiceApi.auto.createInvoiceAndCredit(
          invoiceId,
          {
            invoice,
            lines,
            invoiceMemo: '',
            autoApplyCredit: false,
            customVatLineCountries: getCustomVatLineCountries(customVatLineCountries)
          }
        )
      }

      await Promise.all([
        syncQboObjs(
          this.pgConnection,
          companyCode,
          { invoice: qboEntities.Invoice, payment: qboEntities.Payment, journalentry: qboEntities.JournalEntry, account: [loanAccount] },
          this.entities
        ),
        this.transform.transformAndSave(companyCode, qboEntities)
      ])
      return this.paymentPlan.activatePaymentPlan(paymentPlanId)
    } catch (error) {
      this.logger.error({ code: 'Failed to convert invoice to loan', error })
      throw error
    }
  }

  async bookInstalmentPaystackPayment ({
    paymentApi,
    transactionAdapter,
    paymentPlanApi,
    paystackTransaction
  }) {
    const error = new Error()
    let paymentCreated = false
    let paygPaymentRowCreated = false
    const { reference, currency, paymentPlanId } = paystackTransaction
    const companyCode = this.CURRENCY_TO_COMPANY_CODE[currency]

    if (!companyCode) {
      error.message = `Country code not found for paystack transaction`
      error.status = 404
      throw error
    }

    const { transaction: paymentTransaction, paymentMethod } = await paymentApi.validatePayment(
      reference,
      'paystack',
      companyCode
    )
    const { metadata, amount: amountInCents, customer, created_at: startDate } = paymentTransaction
    const amount = amountInCents / 100

    // we need the paymentPlanId stored in the payment transaction to know which locations payment plan to book payment for
    // we can also use the paymentPlanId sent in the received paystack transaction typically from agave
    if (!metadata.paymentPlanId && !paymentPlanId) {
      error.message = `Payment plan id not found/received for paystack transaction`
      error.status = 404
      throw error
    }

    const paymentPlan = await paymentPlanApi.paymentPlanAdapter.getOne(metadata.paymentPlanId || paymentPlanId)
    const { results: instalments } = await transactionAdapter.getPaymentPlanWithInstalments({
      locationId: paymentPlan.location_id,
      paymentPlanId: paymentPlan.id
    })

    const { results } = await this.location.getList({
      ordering: '-created_at',
      filter: {
        location_id: paymentPlan.location_id,
        company_code: paymentPlan.company_code
      },
      limit: 1
    })
    const quickBooksLocation = results[0]
    const existingDeposit = await this.getExistingDeposit({
      startDate,
      reference,
      companyCode: quickBooksLocation.company_code,
      customerId: quickBooksLocation.quickbooks_customer_id
    })

    if (!existingDeposit) {
      await this.createPaymentPlanDeposit({
        transaction: null,
        paymentPlan,
        paymentInstallment: {
          amount,
          description: reference
        },
        isPaystackDeposit: true,
        instalments,
        qboLocation: quickBooksLocation
      })

      paymentCreated = true
      const {Account: account} = await this.quickbooksInterface.get(
        paymentPlan.company_code,
        `/account/${paymentPlan.quickbooks_account_id}`
      )
      await syncPaymentPlanBalances(
        this.pgConnection, [{
          origin: 'qbo',
          company: paymentPlan.company_code,
          accountId: account.Id,
          balance: account.CurrentBalance
        }]
      )
    }

    const paygPaymentData = await paymentApi.paygCreditPayment.getPaystackPaygPayment(paymentTransaction.id)
    if (!paygPaymentData.length) {
      await paymentApi.paygCreditPayment.savePayment({
        id: v4(),
        location_id: paymentPlan.location_id,
        payment_provider: `PAYSTACK_${companyCode}`.toUpperCase(),
        payment_method: paymentMethod,
        currency: paymentTransaction.currency,
        amount_in_cents: paymentTransaction.amount,
        created_by: customer.email,
        payment_data: {
          trans: `${paymentTransaction.id}`,
          status: paymentTransaction.status,
          trxref: reference,
          message: paymentTransaction.gateway_response,
          reference: reference,
          redirecturl: `?trxref=${reference}&reference=${reference}`,
          transaction: `${paymentTransaction.id}`,
          paymentPlanId: paymentPlan.id
        }
      })
      paygPaymentRowCreated = true
    }

    return {
      message: paymentCreated ? 'Paystack payment booked' : 'Paystack payment already booked prior',
      transactionId: `${paymentTransaction.id}`,
      paymentPlanId: paymentPlan.id,
      meta: { paygPaymentRowCreated, quickbooksDepositCreated: paymentCreated }
    }
  }

  async bookInvoicePaystackPayment ({ paymentApi, paystackTransaction, transactionId }) {
    const error = new Error()
    let paymentCreated = false
    let paygPaymentRowCreated = false
    const { reference, currency, qboCreatedDate, qboDocNumber } = paystackTransaction
    const companyCode = this.CURRENCY_TO_COMPANY_CODE[currency]

    if (!companyCode) {
      error.message = `Country code not found for paystack transaction`
      error.status = 404
      throw error
    }

    const { transaction: paymentTransaction, paymentMethod } = await paymentApi.validatePayment(
      reference,
      'paystack',
      companyCode
    )
    const { metadata, amount: amountInCents, customer } = paymentTransaction
    const amount = amountInCents / 100

    // we need the createdAt timestamp of the invoice and its docNumber to associate the payment to given invoice
    if ((!metadata.qboCreatedDate || !metadata.qboDocNumber) && (!qboDocNumber || !qboCreatedDate)) {
      error.message = `Invoice docNumber & createdAt timestamp not found/received in paystack transaction`
      error.status = 404
      throw error
    }

    const quickbooksLocation = await this.location.getInvoiceLocationTxnData(metadata.qboCreatedDate || qboCreatedDate, metadata.qboDocNumber || qboDocNumber, transactionId)
    const invoiceArray = []
    const paymentArray = []
    const qboInvoice = await this.entities.invoice.get(quickbooksLocation.company_code, quickbooksLocation.txn_id)
    const existingPayment = await this.tools.getInvoiceLinkedPayments(reference, this.entities.payment, quickbooksLocation.company_code, qboInvoice.LinkedTxn)

    // if there is no already linked payment to the invoice for given transaction reference, we proceed with booking
    if (!existingPayment) {
      const {
        payment: paymentResponse
      } = await paymentApi.paymentAdapter.createInvoicePayment({
        quickbooksLocation,
        qboInvoice,
        privateNote: reference,
        paymentAmount: amount
      })
      paymentCreated = true
      paymentArray.push(paymentResponse)
      const updatedInvoice = await this.entities.invoice.get(quickbooksLocation.company_code, quickbooksLocation.txn_id)
      invoiceArray.push(updatedInvoice)

      await Promise.all([
        this.transform.transformAndSave(quickbooksLocation.company_code, { Invoice: invoiceArray, Payment: paymentArray }),
        syncQboObjs(this.pgConnection, quickbooksLocation.company_code, { invoice: invoiceArray, payment: paymentArray }, this.entities)
      ])
    } else {
      // if payments exist for given reference just transform it to the transaction table
      paymentArray.push(existingPayment)
      invoiceArray.push(qboInvoice)
      await Promise.all([
        this.transform.transformAndSave(quickbooksLocation.company_code, { Invoice: invoiceArray, Payment: paymentArray }),
        syncQboObjs(this.pgConnection, quickbooksLocation.company_code, { invoice: invoiceArray, payment: paymentArray }, this.entities)
      ])
    }

    const paygPaymentData = await paymentApi.paygCreditPayment.getPaystackPaygPayment(paymentTransaction.id)
    if (!paygPaymentData.length) {
      await paymentApi.paygCreditPayment.savePayment({
        id: v4(),
        location_id: quickbooksLocation.location_id,
        payment_provider: `PAYSTACK_${companyCode}`.toUpperCase(),
        payment_method: paymentMethod,
        currency: paymentTransaction.currency,
        amount_in_cents: paymentTransaction.amount,
        created_by: customer.email,
        payment_data: {
          trans: `${paymentTransaction.id}`,
          status: paymentTransaction.status,
          trxref: reference,
          message: paymentTransaction.gateway_response,
          reference: reference,
          redirecturl: `?trxref=${reference}&reference=${reference}`,
          transaction: `${paymentTransaction.id}`
        },
        qbo_txn_id: quickbooksLocation.txn_id,
        qbo_company_code: quickbooksLocation.company_code
      })
      paygPaymentRowCreated = true
    }

    return {
      message: paymentCreated ? 'Paystack payment booked' : 'Paystack payment already booked prior',
      transactionId: `${paymentTransaction.id}`,
      qboTxnId: quickbooksLocation.txn_id,
      companyCode: quickbooksLocation.company_code,
      meta: { paygPaymentRowCreated, quickbooksPaymentCreated: paymentCreated }
    }
  }
}

module.exports = QuickbooksAdapter
